import { IEquipment } from '../../../../equipment.types.ts'
import { ISVGSettings } from '@/pages/Dashboards/Charts/SVGChart/svgChart.types'
import { ISVGChartWidget } from '@/pages/Dashboards/dashboards.types'
import parameterIdToSourceIdMap from '@/pages/Parameters/parameters.map'
import { IComputedParameterDef, IParameter } from '@/pages/Parameters/parameters.types'
import liftCardComputeFunctions from './liftCard.computeFunctions'

export interface ILiftCardParameters<TValue> {
  speedWhole: TValue
  speedFractional: TValue | null
  destinationLevel: TValue
  ksmContactor: TValue
  safetyChain: TValue
  kdsRelay: TValue
  k7Relay: TValue
  weight15: TValue
  weight90: TValue
  weight110: TValue
}

export interface ILiftCardLabels {
  speed: string
  destination: string
  ksmContactor: string
  kdsRelay: string
  k7Relay: string
}

export interface ILiftCardProtocolConfig<TValue> {
  labels: ILiftCardLabels
  parameters: ILiftCardParameters<TValue>
}

const lpLiftConfig: ILiftCardProtocolConfig<IParameter['sourceId']> = {
  labels: {
    speed: 'Скорость привода',
    destination: 'Этаж назначения',
    ksmContactor: 'Контактор КМС',
    kdsRelay: 'Реле КДС',
    k7Relay: 'Реле K7',
  },
  parameters: {
    speedWhole: 'carSpeedWhole',
    speedFractional: 'carSpeedFractional',
    destinationLevel: 'destinationLevel',
    ksmContactor: 'ONKMCSgl',
    safetyChain: 'safetyChainState',
    kdsRelay: 'KDSRelayState',
    k7Relay: 'K7RelayState',
    weight15: 'w15kgSgl',
    weight90: 'w90pSgl',
    weight110: 'w110pSgl',
  },
}

const lkdsConfig: ILiftCardProtocolConfig<IParameter['sourceId']> = {
  labels: {
    speed: 'Движение кабины',
    destination: 'Текущая остановка',
    ksmContactor: 'Реле движения',
    kdsRelay: 'Реле контроля дверей',
    k7Relay: 'Блокировка реле движения',
  },
  parameters: {
    speedWhole: 'cabinMoving',
    speedFractional: null,
    destinationLevel: 'currentStop',
    ksmContactor: 'drivingRelay',
    safetyChain: 'safetyChainEnd',
    kdsRelay: 'doorControlRelay',
    k7Relay: 'movingRelayBlock',
    weight15: 'weight15kgSignal',
    weight90: 'weight90PercentsSignal',
    weight110: 'weight110PercentsSignal',
  },
}

const generateWidgetConfig = (svgSettings: ISVGSettings): ISVGChartWidget => {
  return {
    id: 1,
    type: 'svg',
    dashboardId: 1,
    createdAt: '',
    updatedAt: '',
    configuration: {
      settings: svgSettings,
      topLeftX: 0,
      topLeftY: 0,
      width: '100%' as any,
      height: '100%' as any,
    },
  }
}

const getProtocolConfig = (protocolId: IEquipment['protocolId']) => {
  return protocolId === 'lkds' ? lkdsConfig : lpLiftConfig
}

const getSourceIds = (protocolId: IEquipment['protocolId']) => {
  return Array.from(new Set(Object.values(getProtocolConfig(protocolId).parameters).filter(Boolean))) as Array<
    IParameter['sourceId']
  >
}

const mapSourceIdsToParameterIds = (
  protocolId: IEquipment['protocolId'],
  sourceIdToIdMap: Record<IParameter['sourceId'], IParameter['id']>
): ILiftCardProtocolConfig<IParameter['id']> => {
  const config = getProtocolConfig(protocolId)

  return {
    labels: config.labels,
    parameters: {
      speedWhole: sourceIdToIdMap[config.parameters.speedWhole],
      speedFractional: config.parameters.speedFractional ? sourceIdToIdMap[config.parameters.speedFractional] : null,
      destinationLevel: sourceIdToIdMap[config.parameters.destinationLevel],
      ksmContactor: sourceIdToIdMap[config.parameters.ksmContactor],
      safetyChain: sourceIdToIdMap[config.parameters.safetyChain],
      kdsRelay: sourceIdToIdMap[config.parameters.kdsRelay],
      k7Relay: sourceIdToIdMap[config.parameters.k7Relay],
      weight15: sourceIdToIdMap[config.parameters.weight15],
      weight90: sourceIdToIdMap[config.parameters.weight90],
      weight110: sourceIdToIdMap[config.parameters.weight110],
    },
  }
}

const parameterPreprocess = (p: IParameter, value: string) => {
  if (p.sourceId === 'currentLevel' || p.sourceId === 'currentStop') {
    return value.trim().padStart(2, '0')
  }

  if (p.sourceId === 'displayCarDirection') {
    if (+value === 2) {
      return '↓'
    }
    if (+value === 1) {
      return '↑'
    }

    return ''
  }

  if (p.sourceId === 'elevatorMovementUp') {
    return +value === 1 ? '↑' : ''
  }

  return value
}

const computed: IComputedParameterDef[] = [
  {
    id: 'computed:isCarInMotion',
    name: 'Кабина находится в движении',
    sourceId: 'isCarInMotion',
    dataType: 'BOOLEAN',
    dependsOn: [parameterIdToSourceIdMap['carSpeedWhole'], parameterIdToSourceIdMap['carSpeedFractional']],
    compute: ({ get }) =>
      liftCardComputeFunctions.computeIsCarInMotion(
        get(parameterIdToSourceIdMap['carSpeedWhole']),
        get(parameterIdToSourceIdMap['carSpeedFractional'])
      ),
  },
  {
    id: 'computed:carSpeed',
    name: 'Скорость кабины',
    sourceId: 'carSpeed',
    dataType: 'FLOAT',
    dependsOn: [parameterIdToSourceIdMap['carSpeedWhole'], parameterIdToSourceIdMap['carSpeedFractional']],
    compute: ({ get }) =>
      liftCardComputeFunctions.computeCarSpeed(
        get(parameterIdToSourceIdMap['carSpeedWhole']),
        get(parameterIdToSourceIdMap['carSpeedFractional'])
      ),
  },
]

const liftCardConfig = {
  computed,
  generateWidgetConfig,
  parameterPreprocess,
  getProtocolConfig,
  getSourceIds,
  mapSourceIdsToParameterIds,
}

export default liftCardConfig
